import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

export const CartDrawer = () => {
  const { drawerOpen, setDrawerOpen, items, subtotal, updateQty, removeItem } =
    useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        className="flex w-full flex-col p-0 sm:max-w-md"
        data-testid="cart-drawer"
      >
        <SheetHeader className="border-b border-slate-200 px-6 py-4">
          <SheetTitle className="flex items-center justify-between font-display text-xl font-black tracking-tight">
            Your Cart
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              {items.length} item{items.length !== 1 && "s"}
            </span>
          </SheetTitle>
          <SheetDescription className="sr-only">
            Review the items in your cart and proceed to checkout.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div
              className="flex h-full flex-col items-center justify-center text-center"
              data-testid="cart-empty"
            >
              <ShoppingBag className="h-12 w-12 text-slate-300" strokeWidth={1.5} />
              <div className="mt-4 font-display text-lg font-bold text-slate-900">
                Your cart is empty
              </div>
              <p className="mt-1 text-sm text-slate-500">
                Add some protection to get started.
              </p>
              <Button
                onClick={() => {
                  setDrawerOpen(false);
                  navigate("/products");
                }}
                data-testid="cart-browse-btn"
                className="mt-6 rounded-sm bg-emerald-500 text-slate-900 hover:bg-emerald-400"
              >
                Browse Products
              </Button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((it) => (
                <li
                  key={`${it.product_id}-${it.plan_id}`}
                  className="border border-slate-200 p-4"
                  data-testid={`cart-item-${it.product_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                        {it.product.category}
                      </div>
                      <div className="mt-1 font-display text-base font-bold tracking-tight text-slate-900">
                        {it.product.name}
                      </div>
                      <div className="text-xs text-slate-500">{it.plan.label}</div>
                    </div>
                    <button
                      onClick={() => removeItem(it.product_id, it.plan_id)}
                      data-testid={`cart-remove-${it.product_id}`}
                      className="p-1 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <div className="inline-flex border border-slate-300">
                      <button
                        onClick={() => updateQty(it.product_id, it.plan_id, it.quantity - 1)}
                        data-testid={`cart-dec-${it.product_id}`}
                        className="px-2 py-1 hover:bg-slate-100"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="min-w-[2rem] px-2 py-1 text-center text-sm font-bold">
                        {it.quantity}
                      </span>
                      <button
                        onClick={() => updateQty(it.product_id, it.plan_id, it.quantity + 1)}
                        data-testid={`cart-inc-${it.product_id}`}
                        className="px-2 py-1 hover:bg-slate-100"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="font-display text-base font-black tracking-tight text-slate-900">
                      ${it.line_total.toFixed(2)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-slate-200 px-6 py-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Subtotal</span>
              <span
                className="font-display text-2xl font-black tracking-tight text-slate-900"
                data-testid="cart-subtotal"
              >
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Taxes calculated at checkout.
            </div>
            <Button
              onClick={goCheckout}
              data-testid="cart-checkout-btn"
              className="mt-4 w-full rounded-sm bg-emerald-500 py-6 font-bold text-slate-900 hover:bg-emerald-400"
            >
              Proceed to Checkout
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
